import { useState } from "react";
import { useNotify, useRefresh } from "ra-core";
import { CalendarPlus, Mail } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { useRoleDataScope } from "../commons/useRoleDataScope";
import { agendarReuniaoPeloGoogle, enviarEmailPeloGoogle, useConexaoGoogle } from "./GoogleConexao";

/**
 * Ações da ficha com a conta Google do vendedor (pedido do Rick, 29/10/2026):
 * enviar e-mail pelo Gmail dele e marcar reunião na Agenda dele, com convite ao
 * cliente e link do Meet. O que sai fica anotado no histórico do contato (a
 * edge function grava a nota).
 */

type Contato = { id: number | string; nome: string; email?: string | null };

const hoje = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());

/** Brasília não tem horário de verão desde 2019: -03:00 fixo. */
export const inicioEmBrasilia = (data: string, hora: string) => `${data}T${hora}:00-03:00`;

const SemConexao = ({ aoFechar }: { aoFechar: () => void }) => (
  <>
    <DialogHeader>
      <DialogTitle>Conta Google não conectada</DialogTitle>
      <DialogDescription>Envio de e-mail e agenda pela conta Google do usuário.</DialogDescription>
    </DialogHeader>
    <DialogFooter>
      <Button type="button" variant="outline" onClick={aoFechar}>
        Fechar
      </Button>
      <Button asChild>
        <Link to="/settings">Conectar em Meu perfil</Link>
      </Button>
    </DialogFooter>
  </>
);

export const AcoesGoogleContato = ({ contato }: { contato: Contato }) => {
  const { currentCrmUserId } = useRoleDataScope();
  const { linha } = useConexaoGoogle(currentCrmUserId);
  const conectado = linha?.situacao === "ok";
  const notify = useNotify();
  const refresh = useRefresh();
  const [aberto, setAberto] = useState<"email" | "reuniao" | null>(null);
  const [enviando, setEnviando] = useState(false);

  const [para, setPara] = useState(contato.email ?? "");
  const [assunto, setAssunto] = useState("");
  const [texto, setTexto] = useState("");

  const [titulo, setTitulo] = useState(`Reunião: ${contato.nome}`);
  const [data, setData] = useState(hoje());
  const [hora, setHora] = useState("10:00");
  const [duracao, setDuracao] = useState(60);
  const [pauta, setPauta] = useState("");
  const [convidar, setConvidar] = useState(!!contato.email);
  const [meet, setMeet] = useState(true);

  const fechar = () => setAberto(null);

  const enviar = async () => {
    setEnviando(true);
    const { erro } = await enviarEmailPeloGoogle({ contato_id: contato.id, para, assunto, texto });
    setEnviando(false);
    if (erro) {
      notify(erro, { type: "error" });
      return;
    }
    notify("E-mail enviado pelo Gmail.");
    setAssunto("");
    setTexto("");
    fechar();
    refresh();
  };

  const agendar = async () => {
    setEnviando(true);
    const { dados, erro } = await agendarReuniaoPeloGoogle({
      contato_id: contato.id,
      titulo,
      inicio: inicioEmBrasilia(data, hora),
      duracao_min: duracao,
      descricao: pauta || undefined,
      convidar: convidar && !!contato.email,
      com_meet: meet,
    });
    setEnviando(false);
    if (erro) {
      notify(erro, { type: "error" });
      return;
    }
    notify(dados?.meet ? "Reunião agendada, com link do Meet." : "Reunião agendada na Agenda Google.");
    fechar();
    refresh();
  };

  const podeEnviar = para.trim() && assunto.trim() && texto.trim() && !enviando;

  return (
    <>
      <Button variant="outline" onClick={() => setAberto("email")}>
        <Mail />
        E-mail
      </Button>
      <Button variant="outline" onClick={() => setAberto("reuniao")}>
        <CalendarPlus />
        Reunião
      </Button>

      <Dialog open={aberto === "email"} onOpenChange={(o) => !o && fechar()}>
        <DialogContent>
          {!conectado ? (
            <SemConexao aoFechar={fechar} />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>E-mail para {contato.nome}</DialogTitle>
                <DialogDescription>Envio pelo Gmail de {linha?.email}. Registro no histórico do contato.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="g-para">Para</Label>
                  <Input id="g-para" type="email" value={para} onChange={(e) => setPara(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="g-assunto">Assunto</Label>
                  <Input id="g-assunto" value={assunto} onChange={(e) => setAssunto(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="g-texto">Mensagem</Label>
                  <Textarea id="g-texto" rows={8} value={texto} onChange={(e) => setTexto(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={fechar}>
                  Cancelar
                </Button>
                <Button type="button" onClick={() => void enviar()} disabled={!podeEnviar}>
                  {enviando ? "Enviando..." : "Enviar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={aberto === "reuniao"} onOpenChange={(o) => !o && fechar()}>
        <DialogContent>
          {!conectado ? (
            <SemConexao aoFechar={fechar} />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Reunião com {contato.nome}</DialogTitle>
                <DialogDescription>Evento na Agenda Google de {linha?.email}. Registro no histórico do contato.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="g-titulo">Título</Label>
                  <Input id="g-titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="g-data">Data</Label>
                    <Input id="g-data" type="date" value={data} onChange={(e) => setData(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="g-hora">Horário</Label>
                    <Input id="g-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="g-duracao">Duração</Label>
                    <select
                      id="g-duracao"
                      className="h-9 w-full rounded-md border bg-transparent px-2 text-sm"
                      value={duracao}
                      onChange={(e) => setDuracao(Number(e.target.value))}
                    >
                      {[30, 45, 60, 90, 120].map((m) => (
                        <option key={m} value={m}>
                          {m < 60 ? `${m} min` : `${m / 60} h`.replace(".5", ",5")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="g-pauta">Pauta</Label>
                  <Textarea id="g-pauta" rows={3} value={pauta} onChange={(e) => setPauta(e.target.value)} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={convidar && !!contato.email} disabled={!contato.email} onCheckedChange={(v) => setConvidar(v === true)} />
                  {contato.email ? `Enviar convite para ${contato.email}` : "Contato sem e-mail: sem convite"}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={meet} onCheckedChange={(v) => setMeet(v === true)} />
                  Link do Google Meet
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={fechar}>
                  Cancelar
                </Button>
                <Button type="button" onClick={() => void agendar()} disabled={enviando || !data || !hora}>
                  {enviando ? "Agendando..." : "Agendar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
